import React, { useMemo, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { getApiBaseUrl } from '../config/api';
import './AgentResetPassword.css';

function ResetPageShell({ children }) {
  return (
    <div className="reset-password-page agent-login-modern">
      <div className="login-background" aria-hidden="true">
        <div className="bg-shapes">
          <div className="shape shape-1" />
          <div className="shape shape-2" />
          <div className="shape shape-3" />
          <div className="shape shape-4" />
        </div>
      </div>
      <div className="login-wrapper">
        <div className="login-card reset-password-card">
          {children}
        </div>
      </div>
    </div>
  );
}

function PageHeader({ title, subtitle, extra }) {
  return (
    <div className="login-header-modern">
      <div className="logo-container">
        <div className="logo-icon-modern" aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M2 17L12 22L22 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M2 12L12 17L22 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
      </div>
      <h1 className="login-title">{title}</h1>
      {subtitle && <p className="login-subtitle">{subtitle}</p>}
      {extra}
    </div>
  );
}

function EyeIcon({ open }) {
  if (open) {
    return (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
        <circle cx="12" cy="12" r="3" />
      </svg>
    );
  }
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  );
}

function PasswordField({
  id,
  label,
  value,
  onChange,
  showPassword,
  onToggleShow,
  disabled,
  hint,
}) {
  return (
    <div className="input-group-modern">
      <div className="input-wrapper">
        <input
          type={showPassword ? 'text' : 'password'}
          id={id}
          name={id}
          value={value}
          onChange={onChange}
          placeholder=" "
          minLength={6}
          required
          disabled={disabled}
          autoComplete="new-password"
          className="input-modern"
          inputMode="text"
          autoCapitalize="off"
          autoCorrect="off"
          spellCheck="false"
        />
        <label htmlFor={id} className="floating-label">
          <span className="label-icon" aria-hidden="true">🔒</span>
          <span className="label-text">{label}</span>
        </label>
        <button
          type="button"
          className="password-toggle-modern"
          onMouseDown={(e) => e.preventDefault()}
          onClick={onToggleShow}
          disabled={disabled}
          aria-label={showPassword ? 'Hide password' : 'Show password'}
          tabIndex={-1}
        >
          <EyeIcon open={showPassword} />
        </button>
        <div className="input-underline" aria-hidden="true" />
      </div>
      {hint && <p className="reset-form-hint">{hint}</p>}
    </div>
  );
}

function passwordStrength(password) {
  if (!password) return { score: 0, label: '' };
  let score = 0;
  if (password.length >= 6) score += 1;
  if (password.length >= 10) score += 1;
  if (/[A-Z]/.test(password) && /[a-z]/.test(password)) score += 1;
  if (/\d/.test(password)) score += 1;
  if (/[^A-Za-z0-9]/.test(password)) score += 1;
  if (score <= 1) return { score: 1, label: 'Weak' };
  if (score <= 3) return { score: 2, label: 'Fair' };
  return { score: 3, label: 'Strong' };
}

const AgentResetPassword = ({ onDone }) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') || '';
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const strength = useMemo(() => passwordStrength(newPassword), [newPassword]);
  const passwordsMatch = newPassword.length >= 6 && confirmPassword.length > 0 && newPassword === confirmPassword;
  const passwordsMismatch = confirmPassword.length > 0 && newPassword !== confirmPassword;

  const goToLogin = () => (onDone ? onDone() : navigate('/agent'));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!token) {
      setError('Invalid or missing reset link. Request a new one from the login page.');
      return;
    }
    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    setLoading(true);
    try {
      const base = getApiBaseUrl(true);
      const url = base ? `${base}/api/agent/reset-password-by-token` : '/api/agent/reset-password-by-token';
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, newPassword }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setSuccess(true);
      } else {
        setError(data.error || 'Failed to reset password');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <ResetPageShell>
        <div className="success-content-modern">
          <div className="success-icon-modern" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
              <polyline points="22 4 12 14.01 9 11.01" />
            </svg>
          </div>
          <h3>Password reset successful</h3>
          <p>Your password has been updated. Use your username and new password to sign in.</p>
          <button type="button" className="close-success-btn-modern" onClick={goToLogin}>
            Go to login
          </button>
        </div>
      </ResetPageShell>
    );
  }

  if (!token) {
    return (
      <ResetPageShell>
        <PageHeader
          title="Invalid reset link"
          subtitle="This link is missing or invalid. For security, reset links expire after 1 hour and are single-use."
          extra={<p className="reset-form-hint">Use “Forgot your password?” on the login page to receive a new link.</p>}
        />
        <div className="reset-password-actions">
          <button type="button" className="submit-button-modern" onClick={goToLogin}>
            <span>Back to login</span>
            <svg className="arrow-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      </ResetPageShell>
    );
  }

  return (
    <ResetPageShell>
      <PageHeader
        title="Set new password"
        subtitle="This link expires in 1 hour. Choose a strong password with at least 6 characters."
      />

      <form className="login-form-modern reset-password-form" onSubmit={handleSubmit} noValidate>
        <PasswordField
          id="new-password"
          label="New password"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          showPassword={showNewPassword}
          onToggleShow={() => setShowNewPassword((v) => !v)}
          disabled={loading}
          hint="Minimum 6 characters"
        />

        {newPassword.length > 0 && (
          <div className="reset-strength" aria-live="polite">
            <div className="reset-strength-bars">
              {[1, 2, 3].map((level) => (
                <span
                  key={level}
                  className={`reset-strength-bar ${strength.score >= level ? `level-${strength.score}` : ''}`}
                />
              ))}
            </div>
            <span className="reset-strength-label">{strength.label}</span>
          </div>
        )}

        <PasswordField
          id="confirm-password"
          label="Confirm new password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          showPassword={showConfirmPassword}
          onToggleShow={() => setShowConfirmPassword((v) => !v)}
          disabled={loading}
        />

        <div className="reset-match-hint" aria-live="polite">
          {confirmPassword.length > 0 && (
            <p className={`reset-form-hint ${passwordsMatch ? 'reset-form-hint-success' : passwordsMismatch ? 'reset-form-hint-error' : ''}`}>
              {passwordsMatch ? '✓ Passwords match' : passwordsMismatch ? '✗ Passwords do not match' : 'Confirm your new password'}
            </p>
          )}
        </div>

        {error && (
          <div className="error-message-modern" role="alert">
            <svg className="error-icon-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
            <span className="error-text">{error}</span>
          </div>
        )}

        <button
          type="submit"
          className="submit-button-modern"
          disabled={loading || newPassword.length < 6 || !confirmPassword || newPassword !== confirmPassword}
        >
          {loading ? (
            <>
              <svg className="spinner-svg" viewBox="0 0 24 24" aria-hidden="true">
                <circle className="spinner-circle" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" strokeDasharray="32" strokeDashoffset="32">
                  <animate attributeName="stroke-dasharray" dur="2s" values="0 32;16 16;0 32;0 32" repeatCount="indefinite" />
                  <animate attributeName="stroke-dashoffset" dur="2s" values="0;-16;-32;-32" repeatCount="indefinite" />
                </circle>
              </svg>
              <span>Resetting…</span>
            </>
          ) : (
            <>
              <span>Reset password</span>
              <svg className="arrow-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <path d="M5 12h14M12 5l7 7-7 7" />
              </svg>
            </>
          )}
        </button>

        <div className="forgot-password-wrapper reset-back-link">
          <button type="button" className="forgot-password-link-modern" onClick={goToLogin} disabled={loading}>
            Back to login
          </button>
        </div>
      </form>
    </ResetPageShell>
  );
};

export default AgentResetPassword;
